import { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { toast } from "sonner";

type AppRole = Database["public"]["Enums"]["app_role"];

const SESSION_TIMEOUT_MS = 48 * 60 * 60 * 1000; // 48 hours

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  discordUsername: string | null;
  discordVerified: boolean;
  profileCreatedAt: string | null;
  roles: AppRole[];
  /** Real role — used for data queries, mutations, RLS */
  isSov: boolean;
  isSeniorOfficer: boolean;
  isOfficer: boolean;
  /** View-as role — respects testRank for UI visibility only */
  viewIsSov: boolean;
  viewIsSeniorOfficer: boolean;
  viewIsOfficer: boolean;
  /** True if the current user is actually a SOV or Captain (ignores testRank) */
  isRealSov: boolean;
  isRealSeniorOfficer: boolean;
  testRank: string | null;
  setTestRank: (rank: string | null) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [discordUsername, setDiscordUsername] = useState<string | null>(null);
  const [discordVerified, setDiscordVerified] = useState(false);
  const [profileCreatedAt, setProfileCreatedAt] = useState<string | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [testRank, setTestRank] = useState<string | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Auto-logout after 48 hours of inactivity ──
  const resetLogoutTimer = useCallback(() => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    logoutTimerRef.current = setTimeout(async () => {
      await supabase.auth.signOut();
    }, SESSION_TIMEOUT_MS);
  }, []);

  useEffect(() => {
    if (!user) {
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      return;
    }
    // Reset timer on user activity
    resetLogoutTimer();
    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    const handler = () => resetLogoutTimer();
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    };
  }, [user, resetLogoutTimer]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        setTimeout(() => {
          fetchProfile(session.user.id);
          fetchRoles(session.user.id);
        }, 0);
        if (event === "SIGNED_IN") {
          logAuditEvent(session.user);

          // If user just finished the assessment as a guest, send them to the
          // results page so they can see their full assessment + appreciation popup.
          let hasPendingQuiz = false;
          try {
            const raw = sessionStorage.getItem("tgh_pending_operator_quiz_v1");
            if (raw) {
              const parsed = JSON.parse(raw);
              hasPendingQuiz = !!(parsed && parsed.result && Array.isArray(parsed.answers));
              if (!hasPendingQuiz) sessionStorage.removeItem("tgh_pending_operator_quiz_v1");
            }
          } catch {
            try { sessionStorage.removeItem("tgh_pending_operator_quiz_v1"); } catch {}
          }
          if (hasPendingQuiz && window.location.pathname !== "/operator-profile") {
            window.location.href = "/operator-profile";
            return;
          }

          // Show welcome toast once per account
          const key = "tgh_welcome_toast_shown";
          if (!localStorage.getItem(key) && !hasPendingQuiz) {
            localStorage.setItem(key, "1");
            setTimeout(() => {
              toast("Welcome to TGH! 🎖️", {
                description: "Take the Operator Assessment to unlock your tactical profile and get the most out of TGH.",
                action: {
                  label: "Take Assessment",
                  onClick: () => { window.location.href = "/operator-profile"; },
                },
                duration: 12000,
              });
            }, 2000);
          }
        }
      } else {
        setDiscordUsername(null);
        setDiscordVerified(false);
        setProfileCreatedAt(null);
        setRoles([]);
      }
      setLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
        fetchRoles(session.user.id);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("discord_username, discord_verified, created_at")
      .eq("user_id", userId)
      .single();
    if (data) {
      setDiscordUsername(data.discord_username);
      setDiscordVerified(data.discord_verified ?? false);
      setProfileCreatedAt(data.created_at);
    }
  };

  const fetchRoles = async (userId: string) => {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    if (data) setRoles(data.map((r) => r.role));
  };

  const logAuditEvent = async (_u: User) => {
    try {
      await supabase.rpc("log_sign_in_event" as any);
    } catch {
      // non-critical
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const isSov = roles.includes("sov");
  const isSeniorOfficer = isSov || roles.includes("fleet_captain");
  const isOfficer = isSeniorOfficer || roles.includes("ops_commander");
  const isRealSov = isSov;
  const isRealSeniorOfficer = isSeniorOfficer;

  // View-as: for UI visibility only (tabs, pages, nav items)
  const viewIsSov = testRank
    ? testRank === "sovereign_operative"
    : isSov;
  const viewIsSeniorOfficer = testRank
    ? testRank === "sovereign_operative" || testRank === "fleet_captain"
    : isSeniorOfficer;
  const viewIsOfficer = testRank
    ? viewIsSeniorOfficer || testRank === "ops_commander"
    : isOfficer;

  return (
    <AuthContext.Provider value={{ user, session, loading, discordUsername, discordVerified, profileCreatedAt, roles, isSov, isSeniorOfficer, isOfficer, viewIsSov, viewIsSeniorOfficer, viewIsOfficer, isRealSov, isRealSeniorOfficer, testRank, setTestRank, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
